import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { PARTNER_SLUG } from '@/shared/lib/partner';
import { SURFACE } from '@/shared/lib/surface';
import { planFor, type Branch, type FlowPlan, type FlowStep, type Segment } from './flow-plan';

export type { Branch, FlowStep, Segment } from './flow-plan';

// Сценарий партнёра читается один раз при загрузке модуля: внутри одного
// сеанса он смениться не может (см. flow-plan.ts).
const PLAN: FlowPlan = planFor(PARTNER_SLUG, SURFACE);
const ROUTES = PLAN.routes;

export const SEGMENTS: Segment[] = PLAN.segments;

/** Занимает ли шаг модуль целиком — без шапки и полосы шагов. */
export const isFullBleed = (step: FlowStep): boolean => PLAN.fullBleedSteps.includes(step);

/** Первый экран сценария. Раньше это всегда были артисты. */
const START: FlowStep = ROUTES.ready[0] as FlowStep;

/** Куда идёт переход: от него зависит, с какой стороны приходит экран. */
export type Direction = 'forward' | 'back';

interface FlowState {
  branch: Branch;
  step: FlowStep;
  furthestStep: FlowStep;
  stepProgress: number;
  direction: Direction;
  isCartOpen: boolean;

  route: () => FlowStep[];
  index: () => number;
  segmentIndex: () => number;
  isReachable: (step: FlowStep) => boolean;
  entryStep: (segment: Segment) => FlowStep | null;
  setBranch: (branch: Branch) => void;
  /**
   * Забыть пройденный путь и начать считать его с текущего шага.
   *
   * Нужно, когда человек начинает новую вещь: пройденное хранится между
   * сборками, и полоса шагов пускала его на «принт» или «размещение»
   * прошлой вещи — туда, где теперь пусто.
   */
  resetProgress: () => void;
  goTo: (step: FlowStep) => void;
  jump: (step: FlowStep) => void;
  next: () => void;
  back: () => void;
  checkout: () => void;
  setStepProgress: (value: number) => void;
  openCart: () => void;
  closeCart: () => void;
}

export const useFlowStore = create<FlowState>()(
  // Шаг и пройденный путь переживают перезагрузку: человек собирал вещь
  // полчаса, случайно обновил страницу — и оказывался на первом экране
  // с потерянным прогрессом.
  persist(
    (set, get) => ({
      branch: 'ready',
      step: START,
      furthestStep: START,
      stepProgress: 0.33,
      direction: 'forward' as Direction,
      isCartOpen: false,

      route: () => ROUTES[get().branch],
      index: () => ROUTES[get().branch].indexOf(get().step),

      segmentIndex: () => SEGMENTS.findIndex((segment) => segment.steps.includes(get().step)),

      isReachable: (step) => {
        const route = get().route();
        const target = route.indexOf(step);
        return target >= 0 && target <= route.indexOf(get().furthestStep);
      },

      entryStep: (segment) => {
        const route = get().route();
        return segment.steps.find((step) => route.includes(step)) ?? null;
      },

      setBranch: (branch) => set({ branch }),

      resetProgress: () => set({ furthestStep: get().step, stepProgress: 0.33 }),

      goTo: (step) => {
        if (!get().isReachable(step)) return;

        const route = get().route();
        const direction: Direction = route.indexOf(step) < get().index() ? 'back' : 'forward';
        set({ step, stepProgress: 1, direction });
      },

      // Переход без оглядки на пройденное — для быстрых ссылок на каталог
      // и артистов. Их разрешено открывать в любой момент: это витрина,
      // а не шаг сборки. Собранный дизайн при этом не трогаем, человек
      // возвращается к нему той же кнопкой.
      jump: (step) => {
        const route = get().route();
        if (!route.includes(step)) return;

        const furthest =
          route.indexOf(get().furthestStep) < route.indexOf(step) ? step : get().furthestStep;
        const direction: Direction = route.indexOf(step) < get().index() ? 'back' : 'forward';
        set({ step, furthestStep: furthest, stepProgress: 1, direction });
      },

      next: () => {
        const route = get().route();
        const target = route[get().index() + 1];
        if (!target) return;

        const furthest =
          route.indexOf(get().furthestStep) < route.indexOf(target) ? target : get().furthestStep;
        set({ step: target, stepProgress: 0.33, furthestStep: furthest, direction: 'forward' });
      },

      back: () => {
        const route = get().route();
        const target = route[get().index() - 1];
        if (target) set({ step: target, stepProgress: 1, direction: 'back' });
      },

      // Оформление — последний шаг маршрута: «order», где форма на месте,
      // или «done», откуда заказ уходит в мини-приложение.
      checkout: () => {
        const step: FlowStep = get().route().includes('order') ? 'order' : 'done';
        set({
          step,
          furthestStep: step,
          stepProgress: 1,
          direction: 'forward',
          isCartOpen: false,
        });
      },

      setStepProgress: (value) => set({ stepProgress: Math.min(1, Math.max(0, value)) }),

      openCart: () => set({ isCartOpen: true }),
      closeCart: () => set({ isCartOpen: false }),
    }),
    {
      // Ключ хранения привязан к партнёру. Без этого сохранённый шаг одного
      // заказчика восстанавливался у другого: виджет открывался посреди
      // чужого сценария, и выглядело это как сломанная вёрстка.
      // Поверхность входит в ключ: путь на сайте и путь в телеграме —
      // разные пути, и сохранённый шаг одного в другом означает экран
      // не из своего сценария.
      name: `ls-flow:${PARTNER_SLUG}@${SURFACE}`,

      // Открытая корзина и доля заполнения полосы — состояние момента,
      // а не путь: сохранять их значит вернуть человека в чужой сеанс.
      partialize: (state) => ({
        branch: state.branch,
        step: state.step,
        furthestStep: state.furthestStep,
      }),

      // Сохранённый шаг может не существовать в нынешнем сценарии: шаг
      // переименовали, ветку переделали, партнёра сменили. Тогда путь
      // начинается заново — это честнее, чем показать экран не из своего
      // сценария и заставить человека гадать, где он очутился.
      merge: (persisted, current) => {
        const saved = persisted as Partial<FlowState> | null;
        const branch: Branch = saved?.branch === 'custom' ? 'custom' : 'ready';
        const route = ROUTES[branch];

        const step = saved?.step && route.includes(saved.step) ? saved.step : START;
        const furthest =
          saved?.furthestStep && route.includes(saved.furthestStep) ? saved.furthestStep : step;

        return { ...current, branch, step, furthestStep: furthest };
      },
    },
  ),
);
